import { useEffect, useMemo, useState } from "react";

import { TimeZoneSettings } from "./TimeZoneSettingsV2/TimeZoneSettings";
import { ParentLayout } from "../AssistantTools/components/ParentLayout";
import { SectionLayout } from "../AssistantTools/components/SectionLayout";
import { SectionItem } from "../AssistantTools/components/SectionItem";
import { MainLayout } from "../AssistantTools/components/MainLayout";
import DocumentCollectionSettings from "../MessagingSettings/DocumentCollectionSettingsV2";
import ChatWelcomeMessages from "../MessagingSettings/ChatWelcomeMessagesV2";
import MessageDelay from "../MessagingSettings/MessageDelayV2";

import WelcomeMessageSettings from "../VoiceSettings/WelcomeMessageSettingsV3/WelcomeMessageSettings";
import BoostKeywords from "../VoiceSettings/BoostKeywordsV2";
import InactivitySettings from "../VoiceSettings/InactivitySettingsV2";
import InterruptionSettings from "../VoiceSettings/InterruptionSettingsV2";
import BackgroundAmbience from "../VoiceSettings/BackgroundAmbience";
import ConfidenceSettings from "../VoiceSettings/ConfidenceSettings";
import CallDurationSettings from "../VoiceSettings/CallSettings/CallDurationSettings";
import VoicemailAndCallOnHoldSettings from "../VoiceSettings/CallSettings/VoicemailAndCallOnHoldSettings";
import VoiceCache from "../VoiceCacheV2/VoiceCache";
import useWhatsappAssistantMode, {
  WhatsappAssistantMode,
} from "../Assistant/WhatsappAssistant/useWhatsappAssistantMode";
import TabsWithPills from "V2.0/common/tabsWithPills";
import FilteringSettings from "../MessagingSettings/FilteringSettings";
import ConversationReminder from "../MessagingSettings/ConversationReminder";
import PastContextSettings from "../PastContextSettings";

enum GeneralSetting {
  OFFICE_HOURS = "Office Hours",
  PAST_CONTEXT = "Conversation History",
}

enum VoiceSetting {
  WELCOME_MESSAGE = "Welcome Message (Calls)",
  KEYWORDS = "Keywords",
  INACTIVITY = "Inactivity",
  INTERRUPTION = "Interruption",
  BACKGROUND_AMBIENCE = "Background Ambience",
  SPEECH_UNCERTAINITY = "Speech Uncertainity",
  CALL_SETTINGS = "Call Duration",
  VOICEMAIL = "Voicemail & On-hold",
  VOICE_CACHE = "Voice Cache",
}

enum ChatSetting {
  WELCOME_MESSAGE = "Welcome Message (Chat)",
  DELAY = "Response Delay",
  DOCUMENT_COLLECTION = "Document Collection",
  FILTERING_SETTINGS = "Response Filters",
  CONVERSATION_REMINDERS = "Conversation Reminders",
}

const WhatsappAssistantModeSetting = {
  [WhatsappAssistantMode.General]: GeneralSetting,
  [WhatsappAssistantMode.Chat]: ChatSetting,
  [WhatsappAssistantMode.Voice]: VoiceSetting,
};

const SettingsRegistry = {
  [GeneralSetting.OFFICE_HOURS]: (props) => <TimeZoneSettings {...props} />,
  [GeneralSetting.PAST_CONTEXT]: (props) => <PastContextSettings {...props} />,

  [VoiceSetting.WELCOME_MESSAGE]: (props) => (
    <WelcomeMessageSettings {...props} />
  ),
  [VoiceSetting.KEYWORDS]: (props) => <BoostKeywords {...props} />,
  [VoiceSetting.INACTIVITY]: (props) => <InactivitySettings {...props} />,
  [VoiceSetting.INTERRUPTION]: (props) => <InterruptionSettings {...props} />,
  [VoiceSetting.BACKGROUND_AMBIENCE]: (props) => (
    <BackgroundAmbience {...props} />
  ),
  [VoiceSetting.SPEECH_UNCERTAINITY]: (props) => (
    <ConfidenceSettings {...props} />
  ),
  [VoiceSetting.CALL_SETTINGS]: (props) => <CallDurationSettings {...props} />,
  [VoiceSetting.VOICEMAIL]: (props) => (
    <VoicemailAndCallOnHoldSettings {...props} />
  ),
  [VoiceSetting.VOICE_CACHE]: (props) => <VoiceCache {...props} />,

  [ChatSetting.WELCOME_MESSAGE]: (props) => <ChatWelcomeMessages {...props} />,
  [ChatSetting.DELAY]: (props) => <MessageDelay {...props} />,
  [ChatSetting.DOCUMENT_COLLECTION]: (props) => (
    <DocumentCollectionSettings {...props} />
  ),
  [ChatSetting.FILTERING_SETTINGS]: (props) => <FilteringSettings {...props} />,
  [ChatSetting.CONVERSATION_REMINDERS]: (props) => (
    <ConversationReminder {...props} />
  ),
};

function getDefaultSettingForAssistantMode(
  assistantMode: WhatsappAssistantMode,
): GeneralSetting | VoiceSetting | ChatSetting {
  const settings = getSettings(assistantMode);
  return settings[0];
}

function getSettings(
  assistantMode: WhatsappAssistantMode,
): Array<GeneralSetting | VoiceSetting | ChatSetting> {
  const settings = Object.values(WhatsappAssistantModeSetting[assistantMode]);
  return settings;
}

function useWhatsappAssistantModeSetting(assistantMode) {
  const [activeSetting, setActiveSetting] = useState<
    GeneralSetting | VoiceSetting | ChatSetting
  >(ChatSetting.WELCOME_MESSAGE);

  useEffect(
    function () {
      const setting = getDefaultSettingForAssistantMode(assistantMode);
      setActiveSetting(setting);
    },
    [assistantMode],
  );

  const value = useMemo(
    function () {
      return {
        activeSetting,
        setActiveSetting,
        assistantModeSettings: getSettings(assistantMode),
      };
    },
    [activeSetting, setActiveSetting, assistantMode],
  );

  return value;
}

function WhatsappAssistantSettings(props) {
  const { voiceSettings } = props;

  const { activeMode, updateActiveMode, whatsappAssistantModesAsTabs } =
    useWhatsappAssistantMode();
  const { activeSetting, setActiveSetting, assistantModeSettings } =
    useWhatsappAssistantModeSetting(activeMode);

  const settings = assistantModeSettings.reduce((acc, setting) => {
    return [
      ...acc,
      { name: setting, href: "#", current: setting === activeSetting },
    ];
  }, []);

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <TabsWithPills
        className="px-6 pt-4 pb-2"
        tabs={whatsappAssistantModesAsTabs}
        onTabChange={updateActiveMode}
      />
      <ParentLayout>
        <SectionLayout>
          {settings.map((setting) => (
            <SectionItem
              sectionName={setting.name}
              isActive={setting.current}
              onSectionClick={() => setActiveSetting(setting.name)}
            />
          ))}
        </SectionLayout>
        <MainLayout>
          {SettingsRegistry[activeSetting]({
            ...props,
            ...voiceSettings,
          })}
        </MainLayout>
      </ParentLayout>
    </div>
  );
}

export { WhatsappAssistantSettings };
